import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert
} from '@mui/material';
import { ExpandMore, Search } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '@clerk/clerk-react';
import {
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx,
  dialogFormActionsSx
} from '../utils/dialogFormLayout';

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:4000'
  : '';

/** Monospace panel: reads clearly on dark theme (not same grey as dialog paper). */
function playerDetailCodeSx(theme, accent, { maxHeight, fontSize, preWrap = false }) {
  const isDark = theme.palette.mode === 'dark';
  const stripe = theme.palette[accent].main;
  return {
    m: 0,
    p: 1.5,
    borderRadius: 1,
    fontFamily: 'ui-monospace, "Cascadia Code", Consolas, monospace',
    fontSize,
    lineHeight: 1.5,
    overflow: 'auto',
    maxHeight,
    whiteSpace: preWrap ? 'pre-wrap' : 'pre',
    wordBreak: preWrap ? 'break-word' : 'normal',
    color: isDark ? alpha(theme.palette.common.white, 0.9) : alpha(theme.palette.common.black, 0.87),
    bgcolor: isDark ? theme.palette.background.default : theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `4px solid ${stripe}`,
    boxShadow: isDark ? `inset 0 1px 0 ${alpha(stripe, 0.25)}` : 'none'
  };
}

const PlayersPanel = () => {
  const { getToken } = useAuth();
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [detailTitlePlayerId, setDetailTitlePlayerId] = useState('');

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailTitlePlayerId('');
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(limit),
        ...(q.trim() ? { q: q.trim() } : {})
      });
      const response = await fetch(`${API_BASE_URL}/api/players?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load players');
      }
      const data = await response.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [getToken, page, limit, q]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = async (playerId) => {
    setDetailTitlePlayerId(playerId);
    setDetailOpen(true);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const token = await getToken();
      const enc = encodeURIComponent(playerId);
      const response = await fetch(`${API_BASE_URL}/api/players/${enc}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load player');
      }
      setDetail(await response.json());
    } catch (e) {
      setDetailError(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const characterIdsForDetail = () => {
    if (!detail) return [];
    const ids = new Set([
      ...Object.keys(detail.factsByCharacter || {}),
      ...Object.keys(detail.historyByCharacter || {})
    ]);
    return [...ids].sort();
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'flex-start'
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="player ID or nickname"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                setQ(searchInput);
              }
            }}
            sx={{
              flex: '1 1 220px',
              minWidth: { xs: '100%', sm: 220 },
              maxWidth: { sm: 420 }
            }}
          />
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={() => {
              setPage(0);
              setQ(searchInput);
            }}
            sx={{ flexShrink: 0, height: 40, alignSelf: 'flex-start' }}
          >
            Search
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Player details: user state, chat history, facts per character.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Player ID</TableCell>
              <TableCell>Nickname</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={28} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No players found
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow key={row.playerId} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {row.playerId}
                  </TableCell>
                  <TableCell>{row.nickname || '—'}</TableCell>
                  <TableCell>
                    {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openDetail(row.playerId)}>
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={limit}
        onRowsPerPageChange={(e) => {
          setLimit(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
      />

      <Dialog
        open={detailOpen}
        onClose={closeDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogFormPaperSx }}
      >
        <DialogTitle sx={dialogFormTitleSx}>
          {detail && !detailLoading ? (
            <>
              {detail.nickname || 'No nickname'} —{' '}
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                {detail.playerId}
              </Box>
            </>
          ) : detailLoading && detailTitlePlayerId ? (
            <>
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Loading
              </Box>
              {' — '}
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                {detailTitlePlayerId}
              </Box>
            </>
          ) : detailError && detailTitlePlayerId ? (
            <>
              <Box component="span" sx={{ color: 'error.main', fontWeight: 500 }}>
                Error
              </Box>
              {' — '}
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                {detailTitlePlayerId}
              </Box>
            </>
          ) : (
            'Player'
          )}
        </DialogTitle>
        <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          {detail && !detailLoading && (
            <Box>
              <Accordion
                defaultExpanded
                variant="outlined"
                disableGutters
                sx={(theme) => ({
                  mt: 1,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.06),
                  '&:before': { display: 'none' },
                  // MUI сбрасывает margin-top у первого развёрнутого аккордеона — из‑за этого шапка «прилипает»
                  '&.Mui-expanded:first-of-type': {
                    marginTop: theme.spacing(1)
                  }
                })}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={600} color="primary">
                    User state
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box
                    component="pre"
                    sx={(theme) =>
                      playerDetailCodeSx(theme, 'primary', {
                        maxHeight: 280,
                        fontSize: '0.8rem'
                      })
                    }
                  >
                    {JSON.stringify(detail.userState, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {characterIdsForDetail().map((cid) => {
                const name = detail.characterNames?.[cid] || cid;
                const facts = detail.factsByCharacter?.[cid] || [];
                const hist = detail.historyByCharacter?.[cid];
                return (
                  <Accordion
                    key={cid}
                    variant="outlined"
                    disableGutters
                    sx={(theme) => ({
                      mt: 1,
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: alpha(
                        theme.palette.secondary.main,
                        theme.palette.mode === 'dark' ? 0.07 : 0.05
                      ),
                      '&:before': { display: 'none' }
                    })}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography fontWeight={600} color="secondary">
                        {name}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        Facts ({facts.length})
                      </Typography>
                      {facts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          None
                        </Typography>
                      ) : (
                        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                          {facts.map((f, i) => (
                            <li key={i}>
                              <Typography variant="body2">
                                [{f.importance}] {f.text}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      )}
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        History
                      </Typography>
                      <Box
                        component="pre"
                        sx={(theme) =>
                          playerDetailCodeSx(theme, 'secondary', {
                            maxHeight: 240,
                            fontSize: '0.75rem',
                            preWrap: true
                          })
                        }
                      >
                        {hist?.messages ? hist.messages : '—'}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={dialogFormActionsSx}>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayersPanel;
